import React, { useState } from 'react'

import { BellCurveChart } from './BellCurveChart'
import { CategoryControls } from './CategoryControls'
import { type MonthlyDistribution, useImportData } from './ImportDataContext'
import { DIST_COLORS } from './importFormStyles'
import { ScoreCard } from './ScoreCard'

interface DataParametersModalProps {
  onClose: () => void
}

export const DataParametersModal = ({ onClose }: DataParametersModalProps) => {
  const { revenue, expenses, bills, setRevenue, setExpenses, setBills, simulatedYear, commitAndRegenerate } = useImportData()
  const [generated, setGenerated] = useState(false)
  const sliderMax = Math.max(revenue.max, expenses.max, bills.max, 20000)
  const netColor = simulatedYear.net >= 0 ? '#16a34a' : '#dc2626'

  const handleChange = (setter: (d: Partial<MonthlyDistribution>) => void) =>
    (d: Partial<MonthlyDistribution>) => { setter(d); setGenerated(false) }

  const handleGenerate = () => { commitAndRegenerate(); setGenerated(true) }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      {/* Inject thumb styles once — avoids dark default thumb on non-Chrome browsers */}
      <style>{`
        input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; width: 11px; height: 11px; border-radius: 50%; background: var(--thumb-color, #888); cursor: pointer; margin-top: -4px; border: 2px solid white; box-shadow: 0 0 0 1px var(--thumb-color, #888); }
        input[type=range]::-moz-range-thumb { width: 11px; height: 11px; border-radius: 50%; background: var(--thumb-color, #888); cursor: pointer; border: 2px solid white; box-shadow: 0 0 0 1px var(--thumb-color, #888); }
      `}</style>
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', width: 600, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Revenue, Opex, and Bills parameters</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: '0 4px', lineHeight: 1, color: '#6b7280' }}>×</button>
        </div>

        {/* Scorecards */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <ScoreCard label='Topline Revenue' value={simulatedYear.revenue} color={DIST_COLORS.revenue} sub='yearly est.' />
          <ScoreCard label='Operating Expenses' value={simulatedYear.expenses} color={DIST_COLORS.expenses} sub='yearly est.' />
          <ScoreCard label='Bills' value={simulatedYear.bills} color={DIST_COLORS.bills} sub='yearly est.' />
          <ScoreCard label='Net' value={simulatedYear.net} color={netColor} sub='yearly est.' />
        </div>

        {/* Bell curve */}
        <BellCurveChart revenue={revenue} expenses={expenses} bills={bills} />

        {/* Controls */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 20, marginTop: 12 }}>
          <CategoryControls label='Topline Revenue' color={DIST_COLORS.revenue} dist={revenue} onChange={handleChange(setRevenue)} sliderMax={sliderMax} />
          <CategoryControls label='Operating Expenses' color={DIST_COLORS.expenses} dist={expenses} onChange={handleChange(setExpenses)} sliderMax={sliderMax} />
          <CategoryControls label='Bills' color={DIST_COLORS.bills} dist={bills} onChange={handleChange(setBills)} sliderMax={sliderMax} />
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className='admin-button' onClick={handleGenerate}>Generate</button>
          <button className='admin-button' onClick={onClose} disabled={!generated}>Accept</button>
        </div>
      </div>
    </div>
  )
}
